// {feed}/snapshot.json (§14.3): unidade atômica do ciclo (P8).
import * as z from 'zod/mini';
import {
  type CandidateNumber,
  CandidatePairSchema,
  type FeedHeader,
  feedHeaderShape,
  IntSchema,
  type Iso,
  IsoSchema,
  type PlaceCode,
  PlaceCodeSchema,
  type UfCode,
  UfCodeSchema,
} from './base';
import { type ProbabilityPayload, ProbabilityPayloadSchema } from './probability';
import { type CommentScope, CommentScopeSchema } from './scope';
import { type PlaceResult, PlaceResultSchema, placeResultShape, type Tally } from './tally';

export interface Race {
  /** Ordem fixa de exibição (menor primeiro). */
  candidates: [CandidateNumber, CandidateNumber];
  /** Arquivo BR do TSE, sem alteração (M30). */
  national: PlaceResult;
  /** 27 UFs + ZZ. */
  places: Record<PlaceCode, PlaceResult>;
}
export interface GovernorRace extends PlaceResult {
  candidates: [CandidateNumber, CandidateNumber];
}
export interface Reconciliation {
  places_sum: Pick<Tally, 'sections_counted' | 'electorate_counted' | 'turnout' | 'valid_votes'>;
  /** Soma das unidades − nacional. */
  diff: { sections: number; valid_votes: number };
  /** Arquivos de UF mais novos que o BR (normal no pico). */
  places_ahead: boolean;
}
export interface Ops {
  /** Build esperado; obedecido só depois da validação (M9). */
  reload_build: string | null;
  /** Texto ≤ 200, texto puro. */
  notice: { text: string; level: 'info' | 'alert'; since: Iso } | null;
  source_status: 'normal' | 'tse_stalled' | 'tse_unreachable' | 'maintenance';
  /** Intervalo sugerido; o cliente limita a [5, 300]. */
  poll_hint_s?: number;
  /**
   * P-33 (K07): lugares cujos números estão parados na última leitura boa, porque um arquivo do TSE não pôde ser lido.
   * `since` = hora dessa leitura. O número do TSE nunca é alterado; o motor não diz a regra.
   */
  held?: Array<{ scope: CommentScope; since: Iso }>;
}
export interface Snapshot extends FeedHeader<'results.snapshot'> {
  races: { president: Race; governor?: Partial<Record<UfCode, GovernorRace>> };
  reconciliation: Reconciliation;
  /** null = camada desligada. */
  probability: ProbabilityPayload | null;
  files: {
    /** "s/{seq'}/municipal.json" (seq' ≤ seq; pode atrasar). */
    municipal: string | null;
    history: string;
    events: string;
    /** "p/{base_seq}/probability.json" */
    probability_detail: string | null;
    /** "insights/index.json" (P-27); ausente ou null = camada de insights desligada. */
    insights?: string | null;
    /** "commentary/index.json" (P-32); ausente ou null = comentários desligados. */
    commentary?: string | null;
  };
  ops: Ops;
}

/** Na validação, lugar faltando vira E_PLACES (I-10), não E_SCHEMA: por isso partialRecord aqui. */
export const RaceSchema = z.object({
  candidates: CandidatePairSchema,
  national: PlaceResultSchema,
  places: z.partialRecord(PlaceCodeSchema, PlaceResultSchema),
});
export const RacePublishedSchema = z.object({
  candidates: CandidatePairSchema,
  national: PlaceResultSchema,
  places: z.record(PlaceCodeSchema, PlaceResultSchema),
});
export const GovernorRaceSchema = z.object({ ...placeResultShape, candidates: CandidatePairSchema });
export const ReconciliationSchema = z.object({
  places_sum: z.object({
    sections_counted: IntSchema,
    electorate_counted: IntSchema,
    turnout: IntSchema,
    valid_votes: IntSchema,
  }),
  diff: z.object({ sections: IntSchema, valid_votes: IntSchema }),
  places_ahead: z.boolean(),
});
/** Estrutura frouxa: os campos de ops inválidos são descartados um a um (I-16, M9), sem recusar o snapshot. */
export const OpsSchema = z.object({
  reload_build: z.nullable(z.string()),
  notice: z.nullable(z.unknown()),
  source_status: z.enum(['normal', 'tse_stalled', 'tse_unreachable', 'maintenance']),
  poll_hint_s: z.optional(z.unknown()),
  held: z.optional(z.unknown()),
});
export const HeldPlaceSchema = z.object({ scope: CommentScopeSchema, since: IsoSchema });
export const NoticeSchema = z.object({
  text: z.string(),
  level: z.enum(['info', 'alert']),
  since: IsoSchema,
});

const FilesSchema = z.object({
  municipal: z.nullable(z.string()),
  history: z.string(),
  events: z.string(),
  probability_detail: z.nullable(z.string()),
  insights: z.optional(z.nullable(z.string())),
  commentary: z.optional(z.nullable(z.string())),
});

export const SnapshotSchema = z.object({
  ...feedHeaderShape('results.snapshot'),
  races: z.object({
    president: RaceSchema,
    /** Chave livre aqui: governador fora das UFs (ZZ) vira E_PLACES (I-10), não E_SCHEMA. */
    governor: z.optional(z.record(z.string(), GovernorRaceSchema)),
  }),
  reconciliation: ReconciliationSchema,
  /** Validado à parte (I-14): bloco inválido vira "erro" e some, sem derrubar o snapshot. */
  probability: z.nullable(z.unknown()),
  files: FilesSchema,
  ops: OpsSchema,
});

/** Schema estrito, só para o JSON Schema da engenharia: o que o publicador deve gravar. */
export const SnapshotPublishedSchema = z.object({
  ...feedHeaderShape('results.snapshot'),
  races: z.object({
    president: RacePublishedSchema,
    governor: z.optional(z.partialRecord(UfCodeSchema, GovernorRaceSchema)),
  }),
  reconciliation: ReconciliationSchema,
  probability: z.nullable(ProbabilityPayloadSchema),
  files: FilesSchema,
  ops: z.object({
    reload_build: z.nullable(z.string()),
    notice: z.nullable(NoticeSchema),
    source_status: z.enum(['normal', 'tse_stalled', 'tse_unreachable', 'maintenance']),
    poll_hint_s: z.optional(z.number()),
    held: z.optional(z.array(HeldPlaceSchema)),
  }),
});
